// ===== Packages =====

import React, {
    useRef,
    useMemo,
    useEffect,
}                                       from 'react';
import { ReactSVG }                     from 'react-svg';
import {
    useDrag,
    useDrop,
    DropTargetMonitor,
}                                       from 'react-dnd';

// ===== Components =====

import ReaderOutlineSection             from '../ReaderOutlineSection';
import Tooltip                          from '../Tooltip';

// ===== Hooks =====

import {
    useTimeout,
}                                       from '../../hooks';

// ===== Enums =====

import {
    DRAGGABLE_COMPONENT_TYPE,
    CURSOR_TARGET,
    TOOLTIP_TYPE,
    USER_ACTION_TYPE,
    INTERACTABLE_OBJECT,
}                                       from '../../enums';
import {
    ZOOM_LEVEL,
}                                       from '../Editor/elements/enums';

// ===== Services =====

import {
    playAudio,
    recordUserAction,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
    resolveOutlineHoverReorderIndex,
}                                       from '../../services';
import useLazyAudioRef                           from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    IReadPostChapterItem,
    ISnackbarItem,
    IPostPathEvent,
    IZoomLevelItem,
}                                       from '../../interfaces';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    EDITOR_CHAPTER_DESCRIPTION_INPUT_ID_PREFIX,
    EDITOR_CHAPTER_TITLE_INPUT_ID_PREFIX,
    HOVER_TARGET_CLASSNAME,
    EDITOR_CHAPTER_TITLE_INPUT_CLASSNAME,
    EDITOR_CHAPTER_DESCRIPTION_INPUT_CLASSNAME,
    OUTLINE_DRAG_INDICATOR_TRANSITION_DURATION,
    OUTLINE_DRAG_INDICATOR_X_OFFSET,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    GRAB_DRAG_INDICATOR_DELAY_DURATION,
    SERIALIZED_TRUE_INITIAL_EDITOR_VALUE,
}                                       from '../../constants/generalConstants';
import KEYCODE                          from '../../constants/keycodes';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Sounds =====

import InputClick                       from '../../sounds/button_click.mp3';
import ObjectGrasp                      from '../../sounds/object_grasp.mp3';
import ObjectThump                      from '../../sounds/object_thump.mp3';

// ===== Images =====

import MoveSign                         from '../../images/move.svg';
import GrabSign                         from '../../images/grab.svg';
import PlusIcon                         from '../../images/editor/plus.svg';
import CautionIcon                      from '../../images/caution.svg';
import PencilIcon                       from '../../images/editor/pencil.svg';
import HighlightIcon                    from '../../images/editor/highlight.svg';
import DragIndicatorIcon                from '../../images/editor/drag-indicator.svg';
import DisabledIcon                     from '../../images/disabled.svg';

// ===== Styles =====

import {
    Container,
    OutlineChapterHead,
    OutlineChapterTitleInputContainer,
    OutlineChapterDescriptionInput,
    OutlineChapterTitleInput,
    OutlineChapterDragIndicator,
    OutlineNumberText,
    OutlineChapterBody,
    AddSectionButton,
    AddSectionButtonIcon,
    ChapterNonEmptyIndicator,
    ChapterAnnotationsIndicator,
    ChapterDisabledIndicator,
}                                       from './styles';

interface Props {
    customCursorActive: boolean,
    hasSound: boolean,
    postId: string,
    write: boolean,
    chapter: IReadPostChapterItem,
    currentSessionId: string | null,
    currentZoomLevel: IZoomLevelItem | null,
    chapters: IReadPostChapterItem[],
    annotationCount: number,
    sectionAnnotationCountsById: Record<string, number>,
    interactionDisabled: boolean,
    updateChapterTitle: (chapterId: string, title: string) => void,
    flushChapterTitle: (chapterId: string, title: string) => void,
    updateChapterDescription: (chapterId: string, description: string) => void,
    flushChapterDescription: (chapterId: string, description: string) => void,
    updateSectionTitle: (chapterId: string, sectionId: string, title: string) => void,
    flushSectionTitle: (chapterId: string, sectionId: string, title: string) => void,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    selectedPostValuePath: number[],
    onChangeSelectedPostValuePath: (event: IPostPathEvent) => void,
    previewMoveChapter: (draggedChapterId: string, droppedIndex: number) => void,
    commitMoveChapter: (draggedChapterId: string, droppedIndex: number, originalIndex: number) => void,
    resetMoveChapterPreview: () => void,
    previewMoveSection: (chapterId: string, draggedSectionId: string, droppedIndex: number) => void,
    commitMoveSection: (
        chapterId: string,
        draggedSectionId: string,
        droppedIndex: number,
        originalIndex: number,
    ) => void,
    resetMoveSectionPreview: () => void,
    addSection: (chapterId: string) => string | null,
    deleteChapter: (chapterIndex: number) => void,
    deleteSection: (chapterId: string, sectionIndex: number) => void,
}
function ReaderOutlineChapter({
    customCursorActive,
    hasSound,
    postId,
    write,
    chapter,
    currentSessionId,
    currentZoomLevel,
    chapters,
    annotationCount,
    sectionAnnotationCountsById,
    interactionDisabled,
    updateChapterTitle,
    flushChapterTitle,
    updateChapterDescription,
    flushChapterDescription,
    updateSectionTitle,
    flushSectionTitle,
    setCursorSigns,
    onCursorEnter,
    onCursorLeave,
    setSnackbarData,
    selectedPostValuePath,
    onChangeSelectedPostValuePath,
    previewMoveChapter,
    commitMoveChapter,
    resetMoveChapterPreview,
    previewMoveSection,
    commitMoveSection,
    resetMoveSectionPreview,
    addSection,
    deleteChapter,
    deleteSection,
}: Props): JSX.Element {
    // ===== General Constants =====

    const SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR = 'Chapter is saving. Please try again in a few seconds.';

    // ===== Refs =====

    const containerRef = useRef<HTMLDivElement | null>(null);
    const descriptionInputRef = useRef<HTMLDivElement>(null);
    const chapterIdRef = useRef<string>(chapter.id);
    const chapterIndexRef = useRef<number>(chapter.index);
    const chapterSectionsRef = useRef(chapter.sections);

    // ----- Sound Clips
    const inputClickClip = useLazyAudioRef({
        src: InputClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dragIndicatorGraspClip = useLazyAudioRef({
        src: ObjectGrasp,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dragIndicatorThumpClip = useLazyAudioRef({
        src: ObjectThump,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ===== State =====

    // Set up drag source for chapter
    const [{ isDragging }, drag, preview] = useDrag(
        () => ({
            type: DRAGGABLE_COMPONENT_TYPE.editorOutlineTitle,
            item: () => ({
                id: chapterIdRef.current,
                index: chapterIndexRef.current,
                originalIndex: chapterIndexRef.current,
            }),
            canDrag: () => !interactionDisabled,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: (item, monitor) => {
                const didDrop = monitor.didDrop();

                if (!didDrop) {
                    resetMoveChapterPreview();
                    return;
                }

                if (item.index !== item.originalIndex) {
                    commitMoveChapter(item.id, item.index, item.originalIndex);
                }
            },
        }),
        [
            interactionDisabled,
            resetMoveChapterPreview,
            commitMoveChapter,
        ],
    );

    // Set up drop target for chapter
    const [, drop] = useDrop<{ id: string, index: number, originalIndex: number }, void, unknown>(
        () => ({
            accept: DRAGGABLE_COMPONENT_TYPE.editorOutlineTitle,
            hover(item, monitor: DropTargetMonitor<{ id: string, index: number, originalIndex: number }, { droppedIndex?: number }>) {
                const { id, index: draggedIndex } = item;
                const hoveredChapterId = chapterIdRef.current;
                const hoveredChapterIndex = chapterIndexRef.current;
                const hoverBoundingRect = containerRef.current?.getBoundingClientRect();
                const clientOffset = monitor.getClientOffset();
                const nextPreviewIndex = resolveOutlineHoverReorderIndex({
                    interactionDisabled,
                    draggedId: id,
                    hoveredId: hoveredChapterId,
                    draggedIndex,
                    hoveredIndex: hoveredChapterIndex,
                    isShallowOver: monitor.isOver({ shallow: true }),
                    isSameContainer: true,
                    hoverTop: hoverBoundingRect?.top,
                    hoverBottom: hoverBoundingRect?.bottom,
                    clientOffsetY: clientOffset?.y,
                });
                if (nextPreviewIndex == null) return;

                previewMoveChapter(id, nextPreviewIndex);
                // Keep the drag item in sync with the optimistic local order.
                // This avoids issuing repeated preview moves while hovering the same row.
                // eslint-disable-next-line no-param-reassign
                item.index = nextPreviewIndex;
            },
        }),
        [
            interactionDisabled,
            previewMoveChapter,
        ],
    );

    // ===== Animation Constants =====

    // Duration (in milliseconds) for outline chapter head to respond to hover over
    const OUTLINE_CHAPTER_HEAD_TRANSITION_DURATION = 200;
    // Duration (in milliseconds) for outline chapter section to respond to hover over
    const OUTLINE_CHAPTER_SECTION_TRANSITION_DURATION = 200;
    // Duration (in milliseconds) for add section button to respond to hover over
    const ADD_SECTION_BUTTON_TRANSITION_DURATION = 150;
    // Duration (in milliseconds) for non-empty chapter indicator to appear
    const CHAPTER_INDICATOR_TRANSITION_DURATION = GRAB_DRAG_INDICATOR_DELAY_DURATION;

    // ===== Methods =====

    /**
     * Manages response to post title or subtitle input hovering over
     * @param e mouse or touch event
     */
    const onInputMouseEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to post title or subtitle input no longer hovering over
     * @param e mouse or touch event
     */
    const onInputMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to title or subtitle input focusing
     */
    const onInputFocus = (): void => {
        // Play Sound
        if (hasSound && inputClickClip.current) {
            inputClickClip.current.pause();
            inputClickClip.current.currentTime = 0;
            playAudio(inputClickClip.current);
        }
    };

    /**
     * Manages determining whether keystroke is enter or escape
     * If enter: moves focus to input
     * If escape: blurs focused input
     * If backspace/delete: removes focused chapter
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter && !interactionDisabled) {
            // Move from outline chapter title field to outline chapter description field
            if (document.activeElement?.classList.contains(EDITOR_CHAPTER_TITLE_INPUT_CLASSNAME)) {
                // Prevents newline character from being placed in next chapter
                e.preventDefault();

                const chapterDescription = document.activeElement?.parentElement?.nextSibling as HTMLDivElement;
                if (chapterDescription) {
                    chapterDescription.focus();
                }
            }

            // We don't want to move focus to the next chapter if the user is in the description field
            // because the description field is not a text input, it is a div that can contain multiple
            // lines of text
        } else if (e.key === KEYCODE.escape && !interactionDisabled) {
            // Blur Focused Input
            (document.activeElement as HTMLInputElement).blur();
        } else if (
            (
                e.key === KEYCODE.backspace
                || e.key === KEYCODE.delete
            )
            && !interactionDisabled
        ) {
            // Delete Outline Chapter
            // Backspace on empty title
            if (
                document.activeElement?.classList.contains(EDITOR_CHAPTER_TITLE_INPUT_CLASSNAME)
                && (document.activeElement as HTMLInputElement).value.length === 0 // Title
                && document.activeElement.parentElement?.nextElementSibling?.innerHTML?.length === 0 // Description
            ) {
                const chapterIndex = parseInt(document.activeElement.id.split('-')[1], 10);
                onDeleteChapter(chapterIndex);
            }

            // Delete Outline Chapter
            // Backspaced on empty description
            if (
                document.activeElement?.classList.contains(EDITOR_CHAPTER_DESCRIPTION_INPUT_CLASSNAME)
                && document.activeElement.innerHTML?.length === 0 // Description
                && (document.activeElement.previousSibling?.firstChild as HTMLInputElement)?.value.length === 0 // Title
            ) {
                const chapterIndex = parseInt(document.activeElement.id.split('-')[1], 10);
                onDeleteChapter(chapterIndex);
            }
        }
    };

    /**
     * Adds a new section to a chapter
     * @param chapterIndex index of chapter to add section to
     */
    const onAddSectionButtonMouseDown = (): void => {
        if (interactionDisabled) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const chapterId = chapters[chapter.index].id;
        const newSectionIndex = chapters[chapter.index].sections ? chapters[chapter.index].sections!.length : 0;
        const sectionId = addSection(chapterId);

        if (!sectionId) {
            return;
        }

        onChangeSelectedPostValuePath({
            path: [chapter.index, newSectionIndex],
        });

        // Record User Action
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.addPostChapterSection,
                sessionId: currentSessionId,
                payload: {
                    postId,
                    chapterId,
                    sectionId,
                },
            });
        }
    };

    /**
     * Removes a chapter from the contents table
     * @param index index of the chapter to remove
     */
    const onDeleteChapter = (chapterIndex: number): void => {
        if (interactionDisabled) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const removed = chapters[chapterIndex];
        deleteChapter(chapterIndex);

        // Record User Action
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.deletePostChapter,
                sessionId: currentSessionId,
                payload: {
                    postId,
                    chapterId: removed.id,
                },
            });
        }
    };

    /**
     * Manages response to section button hovering over
     * @param e mouse event
     */
    const onAddSectionButtonEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.addSectionButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to section button no longer hovering over
     * @param e mouse event
     */
    const onAddSectionButtonLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to title input changing
     * * @param e input event
     */
    const onOutlineTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        updateChapterTitle(chapter.id, e.target.value);
    };

    const onOutlineTitleInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        flushChapterTitle(chapter.id, e.target.value);
    };

    /**
     * Manages response to editable div changing
     * @param e form event
     */
    const onOutlineDescriptionInputChange = (e: React.FormEvent<HTMLDivElement>): void => {
        updateChapterDescription(chapter.id, (e.target as HTMLDivElement).textContent!);
    };

    const onOutlineDescriptionInputBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
        flushChapterDescription(chapter.id, e.currentTarget.textContent || '');
    };

    /**
     * Manages response to drag indicator hovering over
     * @param e mouse or touch event
     */
    const onDragIndicatorMouseEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.dragIndicator,
            [CURSOR_SIGN.grab],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to drag indicator no longer hovering over
     * @param e mouse or touch event
     */
    const onDragIndicatorMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to drag indicator being selected
     * @param e mouse or touch event
     */
    const onDragIndicatorMouseDown = (e: React.MouseEvent | React.TouchEvent): void => {
        // Changes Cursor
        clearTimeoutRevealMoveAction();
        timeoutRevealMoveAction();

        // Play Sound
        if (hasSound && dragIndicatorGraspClip.current) {
            dragIndicatorGraspClip.current.pause();
            dragIndicatorGraspClip.current.currentTime = 0;
            playAudio(dragIndicatorGraspClip.current);
        }
    };

    /**
     * Manages response to change in chapter/section position
     * @param type chapter or section object
     */
    const onDragIndicatorMouseMove = (
        type: USER_ACTION_TYPE,
    ): void => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages response to drag indicator being released
     */
    const onDragIndicatorMouseUp = (): void => {
        clearTimeoutRevealMoveAction();
        // We still want to show cursor action,
        // but because object no longer held
        // we switch to click action
        setCursorSigns([GrabSign]);

        if (hasSound && dragIndicatorThumpClip.current) {
            dragIndicatorThumpClip.current.pause();
            dragIndicatorThumpClip.current.currentTime = 0;
            playAudio(dragIndicatorThumpClip.current);
        }
    };

    /**
     * Manages modifying selected post value path to this chapter
     */
    const selectChapter = (): void => {
        if (interactionDisabled) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const chapterHasSections = !!chapter.sections && chapter.sections.length > 0;
        const zoomLevelFive = !!currentZoomLevel && currentZoomLevel.level === ZOOM_LEVEL.five;
        if (
            write
            && chapterHasSections
            && zoomLevelFive
        ) {
            onChangeSelectedPostValuePath({
                path: [chapter.index, 0],
                expandItems: [{
                    chapterIndex: chapter.index,
                    expand: true,
                }],
            });
            return;
        }
        if (
            selectedPostValuePath[0] !== chapter.index
            && (
                !chapterHasSections
                || (
                    currentZoomLevel
                    && currentZoomLevel.level < ZOOM_LEVEL.five
                )
            )
        ) {
            onChangeSelectedPostValuePath({
                path: [chapter.index],
            });
        }
    };

    // ===== Side Effects =====

    /**
     * Loads all page sound files into audio elements
     */
    useEffect(() => {
        chapterIdRef.current = chapter.id;
        chapterIndexRef.current = chapter.index;
        chapterSectionsRef.current = chapter.sections;
    }, [
        chapter.id,
        chapter.index,
        chapter.sections,
    ]);

    /**
     * Injects chapter description into editable div
     */
    useEffect(() => {
        if (
            descriptionInputRef.current
            && chapter.description.length > 0
            && descriptionInputRef.current.textContent === ''
        ) {
            descriptionInputRef.current.textContent = chapter.description;
        }
    }, []);

    /**
     * Manages delay of showing move cursor action
     * when drag indicator selected
     */
    const {
        start: timeoutRevealMoveAction,
        clear: clearTimeoutRevealMoveAction,
    } = useTimeout(() => {
        setCursorSigns([MoveSign]);
    }, GRAB_DRAG_INDICATOR_DELAY_DURATION);

    // ===== Memoizations =====

    const nonEmptyIndicatorVisible = useMemo(() => (
        !!chapter.value && chapter.value.length > SERIALIZED_TRUE_INITIAL_EDITOR_VALUE.length
    ), [
        chapter.value,
    ]);

    const isSelected = useMemo(
        () => selectedPostValuePath.length === 1
            && selectedPostValuePath[0] === chapter.index,
        [
            selectedPostValuePath,
            chapter.index,
        ],
    );

    // ===== Render =====

    return (
        <Container
            // Ensures that drag preview and drop target is entire chapter
            ref={(node) => {
                containerRef.current = node;
                if (node) {
                    drop(preview(node));
                }
            }}
            isDragging={isDragging}
        >
            <OutlineChapterHead
                disabled={interactionDisabled}
                selected={isSelected}
                multipleChapters={chapters.length > 1}
                transitionDuration={OUTLINE_CHAPTER_HEAD_TRANSITION_DURATION}
                {...getPressEventHandlers({
                    onTouchStart: !interactionDisabled ? selectChapter : undefined,
                    onMouseDown: !interactionDisabled ? selectChapter : undefined,
                })}
            >
                <OutlineNumberText>
                    {chapter.index + 1}
                </OutlineNumberText>
                <OutlineChapterTitleInputContainer>
                    <OutlineChapterTitleInput
                        type="text"
                        id={`${EDITOR_CHAPTER_TITLE_INPUT_ID_PREFIX}-${chapter.index}`}
                        className={`${HOVER_TARGET_CLASSNAME} ${EDITOR_CHAPTER_TITLE_INPUT_CLASSNAME}`}
                        customCursorActive={customCursorActive}
                        defaultValue={chapter.title}
                        placeholder="Chapter Title"
                        onKeyDown={checkForEnter}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onInputMouseEnter,
                            onMouseLeave: onInputMouseLeave,
                        })}
                        onFocus={onInputFocus}
                        onChange={(e) => onOutlineTitleInputChange(e)}
                        onBlur={onOutlineTitleInputBlur}
                    />
                </OutlineChapterTitleInputContainer>
                <OutlineChapterDescriptionInput
                    contentEditable
                    ref={descriptionInputRef}
                    id={`${EDITOR_CHAPTER_DESCRIPTION_INPUT_ID_PREFIX}-${chapter.index}`}
                    className={`${HOVER_TARGET_CLASSNAME} ${EDITOR_CHAPTER_DESCRIPTION_INPUT_CLASSNAME}`}
                    customCursorActive={customCursorActive}
                    defaultValue={chapter.description}
                    data-placeholder="Description"
                    onKeyDown={checkForEnter}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onInputMouseEnter,
                        onMouseLeave: onInputMouseLeave,
                    })}
                    onFocus={onInputFocus}
                    onInput={(e) => onOutlineDescriptionInputChange(e)}
                    onBlur={onOutlineDescriptionInputBlur}
                />
                {chapters.length > 1 && !interactionDisabled && (
                    <OutlineChapterDragIndicator
                        ref={drag}
                        className={HOVER_TARGET_CLASSNAME}
                        horizontalOffset={OUTLINE_DRAG_INDICATOR_X_OFFSET}
                        transitionDuration={OUTLINE_DRAG_INDICATOR_TRANSITION_DURATION}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onDragIndicatorMouseEnter,
                            onMouseLeave: onDragIndicatorMouseLeave,
                            onTouchMove: () => (!interactionDisabled ? onDragIndicatorMouseMove(USER_ACTION_TYPE.grabOutlineChapter) : undefined),
                            onMouseMove: () => (!interactionDisabled ? onDragIndicatorMouseMove(USER_ACTION_TYPE.grabOutlineChapter) : undefined),
                            onTouchEnd: !interactionDisabled ? onDragIndicatorMouseUp : undefined,
                            onMouseUp: !interactionDisabled ? onDragIndicatorMouseUp : undefined,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: !interactionDisabled ? onDragIndicatorMouseDown : undefined,
                            onMouseDown: !interactionDisabled ? onDragIndicatorMouseDown : undefined,
                        })}
                    >
                        <ReactSVG
                            src={DragIndicatorIcon}
                        />
                    </OutlineChapterDragIndicator>
                )}
                <ChapterNonEmptyIndicator
                    isVisible={nonEmptyIndicatorVisible}
                    transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
                >
                    <Tooltip
                        text="Chapter has content"
                        side={TOOLTIP_TYPE.left}
                    />
                    <ReactSVG
                        src={PencilIcon}
                    />
                </ChapterNonEmptyIndicator>
                <ChapterAnnotationsIndicator
                    isVisible={annotationCount > 0}
                    nonEmptyIndicatorVisible={nonEmptyIndicatorVisible}
                    transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
                >
                    <Tooltip
                        text="Chapter has annotations"
                        side={TOOLTIP_TYPE.left}
                    />
                    <ReactSVG
                        src={HighlightIcon}
                    />
                </ChapterAnnotationsIndicator>
                {isSelected && !chapter.sections && (
                    <ChapterDisabledIndicator
                        isVisible={interactionDisabled}
                        nonEmptyIndicatorVisible={nonEmptyIndicatorVisible}
                        annotationsIndicatorVisible={annotationCount > 0}
                        transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
                    >
                        <Tooltip
                            text="Post is being updated"
                            side={TOOLTIP_TYPE.left}
                        />
                        <ReactSVG
                            src={DisabledIcon}
                        />
                    </ChapterDisabledIndicator>
                )}
            </OutlineChapterHead>
            {currentZoomLevel
            && currentZoomLevel.level === ZOOM_LEVEL.five
            && (
                <OutlineChapterBody>
                    {chapter.sections && (
                        chapter.sections.map((section) => (
                            <ReaderOutlineSection
                                key={section.id}
                                customCursorActive={customCursorActive}
                                postId={postId}
                                currentSessionId={currentSessionId}
                                chapterIndex={chapter.index}
                                numSections={chapter.sections!.length}
                                section={section}
                                annotationCount={sectionAnnotationCountsById[section.id] || 0}
                                transitionDuration={OUTLINE_CHAPTER_SECTION_TRANSITION_DURATION}
                                chapters={chapters}
                                sections={chapter.sections!}
                                interactionDisabled={interactionDisabled}
                                updateSectionTitle={updateSectionTitle}
                                flushSectionTitle={flushSectionTitle}
                                onDragIndicatorMouseEnter={onDragIndicatorMouseEnter}
                                onDragIndicatorMouseLeave={onDragIndicatorMouseLeave}
                                onDragIndicatorMouseDown={onDragIndicatorMouseDown}
                                onDragIndicatorMouseUp={onDragIndicatorMouseUp}
                                onDragIndicatorMouseMove={onDragIndicatorMouseMove}
                                onInputMouseEnter={onInputMouseEnter}
                                onInputMouseLeave={onInputMouseLeave}
                                onInputFocus={onInputFocus}
                                setSnackbarData={setSnackbarData}
                                selectedPostValuePath={selectedPostValuePath}
                                onChangeSelectedPostValuePath={onChangeSelectedPostValuePath}
                                previewMoveSection={previewMoveSection}
                                commitMoveSection={commitMoveSection}
                                resetMoveSectionPreview={resetMoveSectionPreview}
                                deleteSection={deleteSection}
                            />
                        )))}
                    {!interactionDisabled && (
                        <AddSectionButton
                            className={HOVER_TARGET_CLASSNAME}
                            disabled={interactionDisabled}
                            chapterSelected={selectedPostValuePath.length === 1 && selectedPostValuePath[0] === chapter.index}
                            bottomSectionSelected={
                                selectedPostValuePath.length === 1
                                || (
                                    selectedPostValuePath.length === 2
                                    && !!chapter.sections
                                    && chapter.sections!.length - 1 === selectedPostValuePath[1]
                                )
                            }
                            transitionDuration={ADD_SECTION_BUTTON_TRANSITION_DURATION}
                            {...getPressEventHandlers({
                                onTouchStart: !interactionDisabled ? onAddSectionButtonMouseDown : undefined,
                                onMouseDown: !interactionDisabled ? onAddSectionButtonMouseDown : undefined,
                            })}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onAddSectionButtonEnter,
                                onMouseLeave: onAddSectionButtonLeave,
                            })}
                        >
                            <Tooltip
                                text="Add Section"
                                side={TOOLTIP_TYPE.right}
                            />
                            <AddSectionButtonIcon>
                                <ReactSVG
                                    src={PlusIcon}
                                />
                            </AddSectionButtonIcon>
                        </AddSectionButton>
                    )}
                </OutlineChapterBody>
            )}
        </Container>
    );
}

export default ReaderOutlineChapter;
